// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from "@stencil/core";
import { Component, Element, Event, h, Prop, State } from "@stencil/core";
import type { LiteralUnion } from "type-fest";

import { onMobileBreakpoint } from "../utils";

export type Target = "_blank" | "_parent" | "_self" | "_top";

@Component({
  tag: "qds-web-megamenu-content-banner",
  shadow: true,
  styleUrl: "megamenu-content-banner.css",
})
export class MegamenuContentBanner implements ComponentInterface {
  @Element() public el: HTMLElement;

  /**
   * Emitted when the corresponding megamenu is closed.
   */

  @Event({ eventName: "qds-nav-item:close" })
  public closeNavTabs: EventEmitter<boolean>;

  /**
   * Emitted when the megamenu is opened.
   */

  @Event({ eventName: "qds-nav-item:open" })
  public openNavTabs: EventEmitter<boolean>;

  /**
   *
   * The banner's button text.
   */
  @Prop() public readonly buttonText?: string;

  /**
   * The banner's title.
   */
  @Prop() public readonly text?: string;

  /**
   * The banner's description paragraphe.
   */
  @Prop() public readonly description?: string;

  /**
   * The banner's button href.
   * @webnative
   */
  @Prop() public readonly href?: string;

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<Target, string>;

  /**
   * The source URL of the banner's image.
   */
  @Prop() public readonly src: string;

  @State() public isMobile: boolean;

  private cleanupMobileListener?: () => void;

  get #hasButtonText(): boolean {
    return this.buttonText !== undefined && this.buttonText !== "";
  }

  get #hasHref(): boolean {
    return this.href !== undefined && this.href !== "";
  }

  get #isSelectable(): boolean {
    return !this.#hasButtonText && this.#hasHref;
  }

  public componentWillLoad(): Promise<void> | void {
    this.cleanupMobileListener = onMobileBreakpoint((isMobile: boolean) => {
      this.isMobile = isMobile;
    }, this.el);
  }

  public disconnectedCallback(): void {
    this.cleanupMobileListener?.();
  }

  public render() {
    return (
      <div
        class={{
          mobile: this.isMobile,
          web: !this.isMobile,
          "qds-web-selectable": this.#isSelectable,
        }}
      >
        <div class="body">
          <h3 class="qds-web-title">
            {this.#isSelectable ? (
              <div>
                <a
                  class="qds-web-title-tag-link"
                  target={this.target}
                  href={this.href}
                  aria-label={this.text}
                />
                <div aria-hidden="true">{this.text}</div>
              </div>
            ) : (
              this.text
            )}
          </h3>
          <p>{this.description}</p>
        </div>

        {this.isMobile ? (
          this.#hasButtonText && (
            <qds-button text={this.buttonText} href={this.href} />
          )
        ) : this.src ? (
          <img src={this.src} alt="" />
        ) : (
          this.#hasButtonText && (
            <div class="button-wrapper">
              <qds-button
                class="button-padding"
                size="large"
                text={this.buttonText}
                href={this.href}
                target={this.target}
                onClick={() => {
                  if (this.target !== "_blank") {
                    this.closeNavTabs.emit(true);
                    this.openNavTabs.emit(false);
                  }
                }}
              />
            </div>
          )
        )}
      </div>
    );
  }
}
